import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'

import { useCloneGameMutation, useUpdateGameMutation } from '../../../../api/core'
import { useAppDispatch } from '../../../../hooks/storeHooks'
import analyticsService from '../../../../services/AnalyticsService'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { displaySnackBar } from '../../../snackbar'
import { SnackBarMessage } from '../../../snackbar/types/SnackBarMessage'
import { Game } from '../../types/Game'
import { getLocalizedCloneFields } from '../../util/localizedCloneFields'
import GameEditModal from '../GameEditModal/GameEditModal'

/**
 * GameCloneDialog Open Dialog confirmation to clone game
 */
interface GameCloneDialogProps {
  open: boolean
  onClose: () => void
  originGame: Game
}
const GameCloneDialog: React.FC<GameCloneDialogProps> = ({ open, onClose, originGame }) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()

  const [newGameClone, setNewGameClone] = useState<Game | undefined>()
  const [cloneGame, { data: clonedGame, isLoading: isGameCloning, isSuccess: isGameCloningSuccess, error: gameCloningError }] = useCloneGameMutation()
  const [updateGame, { isLoading: isGameUpdating }] = useUpdateGameMutation()
  const dispatch = useAppDispatch()
  const showError = useCallback((errorPayload: SnackBarMessage) => dispatch(displaySnackBar(errorPayload)), [dispatch])
  const navigate = useNavigate()

  const onGameCloneClick = () => {
    cloneGame({ gameId: originGame.id, userLanguage })
  }

  const onGameEditDone = useCallback(
    (game: Game, isGameDeleted?: boolean) => {
      if (!isGameDeleted) {
        navigate(`/game-analyzer/${game.id}`, { replace: true })
        return
      }
      navigate('/game-analyzer')
      onClose()
    },
    [navigate, onClose]
  )

  useEffect(() => {
    if (isGameCloningSuccess && clonedGame) {
      analyticsService.trackEvent('Cloned game', { data: { clonedGameId: clonedGame.id, gameId: originGame.id, gameName: originGame.resolvedName } })

      // GR-1442: the backend builds the clone's name/desc from the origin's default-language
      // values, so for ja/zh users they don't match the game view page. Persist the
      // user-language values so that Game Analyzer (which falls back to the single name/desc
      // for own games) matches too. The " (Clone)" suffix and any other surrounding text is
      // preserved to avoid re-introducing GR-1521.
      const localizedFields = getLocalizedCloneFields(clonedGame, originGame)

      if (!localizedFields) {
        setNewGameClone(clonedGame)
        return
      }

      // Build a fresh plain data object instead of mutating the RTK Query cache object.
      // It must stay a plain object for the PUT body: fetchBaseQuery only JSON-serializes
      // plain objects, so passing a Game class instance would send "[object Object]".
      const localizedCloneData = { ...clonedGame.gameData, ...localizedFields } as Game

      updateGame({ game: localizedCloneData, gameId: clonedGame.id })
        .unwrap()
        .then(() => setNewGameClone(new Game(localizedCloneData, { userLanguage })))
        // Graceful degradation: the clone itself succeeded, so open the modal with the
        // server values when persisting the localized fields fails.
        .catch(() => setNewGameClone(clonedGame))
    }
  }, [clonedGame, isGameCloningSuccess, originGame, updateGame, userLanguage])

  useEffect(() => {
    if (gameCloningError) {
      showError({ message: t('game-header:clone_error_body'), severity: 'error', open: true })
    }
  }, [gameCloningError, showError, t])

  const onGameEditClose = () => {
    setNewGameClone(undefined)
    onClose()
  }

  return (
    <>
      {!!newGameClone && <GameEditModal onGameEditDone={onGameEditDone} game={newGameClone} open={!!newGameClone} onClose={onGameEditClose} />}
      <Dialog open={open}>
        <DialogTitle>{t('game-header:clone_game')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Trans i18nKey="game-header:confirm_clone_text" values={{ gameName: originGame.resolvedName }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" disabled={isGameCloning || isGameUpdating} onClick={() => onGameCloneClick()}>
            {t('common:yes')}
          </Button>
          <Button disabled={isGameCloning || isGameUpdating} onClick={() => onClose()}>
            {t('common:no')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default GameCloneDialog
